import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, MapPin, CheckCircle } from 'lucide-react';
import { Location } from '@/firebase/locationService';
import { locationService } from '@/firebase/locationService';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNotification } from '@/contexts/NotificationContext';
import { ComplianceTemplate } from '@/firebase/complianceService';
import { auditLogService } from '@/services/auditLogService';

interface AssignLocationsToTemplatePanelProps {
  template: ComplianceTemplate;
  onClose: () => void;
  onAssignComplete?: () => void;
}

const AssignLocationsToTemplatePanel: React.FC<AssignLocationsToTemplatePanelProps> = ({ 
  template, 
  onClose,
  onAssignComplete
}) => {
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [selectedLocationIds, setSelectedLocationIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedState, setSelectedState] = useState<string>('All States');

  useEffect(() => {
    loadLocations();
  }, [currentOrganization?.id]);

  const loadLocations = async () => {
    if (!currentOrganization?.id) return;
    
    setIsLoading(true);
    try {
      const result = await locationService.getAllLocations(currentOrganization.id);
      if (result.success && result.data) {
        setLocations(result.data);
        
        // Pre-select locations that already have this template assigned
        const alreadyAssigned = new Set<string>();
        result.data.forEach(loc => {
          if (loc.id && loc.assignedTemplates?.includes(template.id || '')) {
            alreadyAssigned.add(loc.id);
          }
        });
        setSelectedLocationIds(alreadyAssigned);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      showNotification('Error loading locations', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Get unique states for filtering
  const states = useMemo(() => {
    const uniqueStates = Array.from(new Set(locations.map(l => l.state).filter(Boolean)));
    return uniqueStates.sort();
  }, [locations]);

  // Filter locations by search term and state
  const filteredLocations = useMemo(() => {
    return locations.filter(loc => {
      const matchesSearch = !searchTerm || 
        loc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        loc.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        loc.address.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesState = selectedState === 'All States' || loc.state === selectedState;
      
      return matchesSearch && matchesState;
    });
  }, [locations, searchTerm, selectedState]);

  const toggleLocation = (locationId: string) => {
    setSelectedLocationIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(locationId)) {
        newSet.delete(locationId);
      } else {
        newSet.add(locationId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allFilteredIds = filteredLocations.map(l => l.id).filter(Boolean) as string[];
      setSelectedLocationIds(prev => new Set([...prev, ...allFilteredIds]));
    } else {
      const allFilteredIds = filteredLocations.map(l => l.id).filter(Boolean) as string[];
      setSelectedLocationIds(prev => {
        const newSet = new Set(prev);
        allFilteredIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    }
  };

  const areAllFilteredSelected = filteredLocations.length > 0 && 
    filteredLocations.every(l => l.id && selectedLocationIds.has(l.id));

  const handleAssign = async () => {
    if (!template.id) {
      showNotification('Template ID not found', 'error');
      return;
    }

    if (selectedLocationIds.size === 0) {
      showNotification('Please select at least one location', 'error');
      return;
    }

    setIsAssigning(true);
    try {
      let successCount = 0;
      let errorCount = 0;

      // Update each selected location
      for (const locationId of selectedLocationIds) {
        const location = locations.find(l => l.id === locationId);
        if (!location) continue;

        const currentTemplates = location.assignedTemplates || [];
        const updatedTemplates = currentTemplates.includes(template.id)
          ? currentTemplates // Already assigned, skip
          : [...currentTemplates, template.id];

        const result = await locationService.updateLocation(
          locationId, 
          {
            assignedTemplates: updatedTemplates,
          },
          undefined, // Don't log as location update
          undefined
        );

        if (result.success) {
          // Log audit entry for compliance assignment
          try {
            const userInfo = user ? { id: user.id, name: user.name, email: user.email } : undefined;
            const orgId = currentOrganization?.id;
            
            if (userInfo && orgId && !currentTemplates.includes(template.id)) {
              const logResult = await auditLogService.log({
                action: 'compliance_assigned',
                entityType: 'compliance_assignment',
                entityId: `${locationId}-${template.id}`,
                entityName: `${template.name} → ${location.name}`,
                userId: userInfo.id,
                userName: userInfo.name,
                userEmail: userInfo.email,
                organizationId: orgId,
                details: {
                  locationId: locationId,
                  locationName: location.name,
                  complianceId: template.id,
                  complianceName: template.name,
                },
              });
              if (logResult.success) {
                console.log('✅ Logged compliance_assigned:', template.name, 'to', location.name);
              } else {
                console.error('❌ Failed to log compliance_assigned:', logResult.error);
              }
            }
          } catch (auditError) {
            console.error('❌ Error logging compliance assignment:', auditError);
            // Don't fail the operation if audit logging fails
          }
          
          successCount++;
        } else {
          errorCount++;
          console.error(`Error assigning template to location ${locationId}:`, result.error);
        }
      }

      // Also remove from locations that were previously selected but are now deselected
      const previouslySelected = locations.filter(loc => 
        loc.id && loc.assignedTemplates?.includes(template.id || '')
      );
      
      for (const location of previouslySelected) {
        if (location.id && !selectedLocationIds.has(location.id)) {
          const currentTemplates = location.assignedTemplates || [];
          const updatedTemplates = currentTemplates.filter(id => id !== template.id);
          
          const result = await locationService.updateLocation(
            location.id, 
            {
              assignedTemplates: updatedTemplates,
            },
            undefined, // Don't log as location update
            undefined
          );

          if (result.success) {
            // Log audit entry for compliance removal
            try {
              const userInfo = user ? { id: user.id, name: user.name, email: user.email } : undefined;
              const orgId = currentOrganization?.id;
              
              if (userInfo && orgId) {
                const logResult = await auditLogService.log({
                  action: 'compliance_removed',
                  entityType: 'compliance_assignment',
                  entityId: `${location.id}-${template.id}`,
                  entityName: `${template.name} → ${location.name}`,
                  userId: userInfo.id,
                  userName: userInfo.name,
                  userEmail: userInfo.email,
                  organizationId: orgId,
                  details: {
                    locationId: location.id,
                    locationName: location.name,
                    complianceId: template.id,
                    complianceName: template.name,
                  },
                });
                if (logResult.success) {
                  console.log('✅ Logged compliance_removed:', template.name, 'from', location.name);
                } else {
                  console.error('❌ Failed to log compliance_removed:', logResult.error);
                }
              } else {
                console.warn('⚠️ Cannot log audit entry: user or organization not available');
              }
            } catch (auditError) {
              console.error('❌ Error logging compliance removal:', auditError);
              // Don't fail the operation if audit logging fails
            }
            
            successCount++;
          } else {
            errorCount++;
            console.error(`Error removing template from location ${location.id}:`, result.error);
          }
        }
      }

      if (errorCount === 0) {
        showNotification(
          `Successfully assigned "${template.name}" to ${successCount} location${successCount !== 1 ? 's' : ''}`,
          'success'
        );
        onAssignComplete?.();
        onClose();
      } else {
        showNotification(
          `Assigned to ${successCount} location${successCount !== 1 ? 's' : ''}, but ${errorCount} error${errorCount !== 1 ? 's' : ''} occurred`,
          'error'
        );
      }
    } catch (error: any) {
      console.error('Error assigning template to locations:', error);
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-end z-[120]">
      <div className="bg-white w-full max-w-2xl h-full flex flex-col shadow-xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Assign to Locations</h2>
              <p className="text-sm text-gray-600 mt-1">{template.name}</p>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
              disabled={isAssigning}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search locations by name, city, or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={isAssigning}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                  disabled={isAssigning}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-md">
              <MapPin className="w-4 h-4 text-gray-600" />
            </div>
            
            {/* State Filter */}
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
              disabled={isAssigning}
            >
              <option value="All States">All States</option>
              {states.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Locations List */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="text-center py-12 text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p>Loading locations...</p>
            </div>
          ) : filteredLocations.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MapPin className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No locations found matching your filters.</p>
            </div>
          ) : (
            <>
              {/* Select All */}
              <div className="mb-4 pb-4 border-b border-gray-200">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={areAllFilteredSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    disabled={isAssigning}
                  />
                  <span className="text-sm font-medium text-gray-900">
                    Select All ({filteredLocations.length} location{filteredLocations.length !== 1 ? 's' : ''})
                  </span>
                </label>
              </div>

              {/* Locations List */}
              <div className="space-y-2">
                {filteredLocations.map(location => {
                  const isSelected = location.id ? selectedLocationIds.has(location.id) : false;
                  const wasAlreadyAssigned = location.assignedTemplates?.includes(template.id || '');
                  
                  return (
                    <label
                      key={location.id}
                      className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-colors ${
                        isSelected 
                          ? 'bg-primary-50 border-2 border-primary-500' 
                          : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-gray-900">
                            {location.name}
                          </div>
                          {wasAlreadyAssigned && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                              Already Assigned
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {location.address}
                          {location.city && `, ${location.city}`}
                          {location.state && `, ${location.state}`}
                          {location.zipCode && ` ${location.zipCode}`}
                        </div>
                        {location.assignedTemplates && location.assignedTemplates.length > 0 && (
                          <div className="text-xs text-gray-400 mt-1">
                            {location.assignedTemplates.length} compliance{location.assignedTemplates.length !== 1 ? 's' : ''} assigned
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        {isSelected ? (
                          <CheckCircle className="w-6 h-6 text-primary-600" />
                        ) : (
                          <div className="w-6 h-6 border-2 border-gray-300 rounded-full"></div>
                        )}
                      </div>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => location.id && toggleLocation(location.id)}
                        className="sr-only"
                        disabled={isAssigning}
                      />
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {selectedLocationIds.size} location{selectedLocationIds.size !== 1 ? 's' : ''} selected
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isAssigning}
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={isAssigning || selectedLocationIds.size === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isAssigning ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Assigning...
                  </>
                ) : (
                  `Assign to ${selectedLocationIds.size} Location${selectedLocationIds.size !== 1 ? 's' : ''}`
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignLocationsToTemplatePanel;


